import { useEffect, useRef, useState } from 'react';
import type { AdminPhoto } from '@mantelpiece/shared';
import { api } from '../lib/api';
import { formatBytes, formatDateTime, shortId } from '../lib/format';
import { href } from '../lib/route';
import { AccountLink, FrameLink, Pill } from './bits';

interface Props {
  readonly token: string;
  readonly photos: readonly AdminPhoto[];
  /** Called after a takedown, so the list that owns these can refresh. */
  readonly onChanged: () => void;
}

const statusTone = (photo: AdminPhoto): 'ok' | 'wait' | 'bad' =>
  photo.deletedAt !== null ? 'bad' : photo.status === 'pending' ? 'wait' : 'ok';

const statusLabel = (photo: AdminPhoto): string =>
  photo.deletedAt !== null ? 'taken down' : photo.status;

/**
 * A contact sheet of placements with a detail dialog behind each print. The
 * dialog is a native `<dialog>` so focus, Escape and the backdrop come for
 * free and read correctly to a screen reader.
 */
export const PhotoGrid = ({ token, photos, onChanged }: Props) => {
  const [open, setOpen] = useState<AdminPhoto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  const takeDown = async (photo: AdminPhoto) => {
    if (!window.confirm(`Take this photo down from “${photo.device.label}”? The sender is not told.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.takeDownPhoto(token, photo.id);
      setOpen(null);
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  if (photos.length === 0) return <p className="empty">No photos.</p>;

  return (
    <>
      <ul className="sheet">
        {photos.map((photo) => (
          <li key={photo.id} className="print" data-state={photo.deletedAt !== null ? 'gone' : 'ok'}>
            <button
              type="button"
              className="print-pick"
              onClick={() => setOpen(photo)}
              aria-label={`${photo.caption ?? 'Untitled'} by ${photo.uploader} on ${photo.device.label}`}
            >
              <span className="print-image">
                <img src={photo.thumbUrl} alt="" loading="lazy" width={photo.width} height={photo.height} />
              </span>
            </button>
            <div className="print-caption">
              <span className="print-frame">{photo.device.label}</span>
              <span className="print-status" data-tone={statusTone(photo)}>
                {statusLabel(photo)}
              </span>
            </div>
            <p className="print-by">{photo.uploader}</p>
          </li>
        ))}
      </ul>

      <dialog
        ref={dialog}
        className="lightbox"
        aria-labelledby="lightbox-title"
        onClose={() => setOpen(null)}
        onClick={(event) => {
          // A click on the backdrop lands on the dialog element itself.
          if (event.target === dialog.current) setOpen(null);
        }}
      >
        {open && (
          <div className="lightbox-body">
            <div className="lightbox-image">
              <img src={open.url} alt={open.caption ?? ''} />
            </div>
            <div className="lightbox-facts">
              <h2 id="lightbox-title" className="section-title">
                {open.caption ?? 'No caption'}
              </h2>
              <dl className="facts">
                <div className="fact">
                  <dt>Status</dt>
                  <dd>
                    <Pill tone={statusTone(open)}>{statusLabel(open)}</Pill>
                  </dd>
                </div>
                <div className="fact">
                  <dt>Frame</dt>
                  <dd>
                    <FrameLink device={open.device} />
                  </dd>
                </div>
                <div className="fact">
                  <dt>Sent by</dt>
                  <dd>
                    {open.uploader}
                    {open.uploaderAccount ? (
                      <>
                        {' '}
                        <AccountLink account={open.uploaderAccount} />
                      </>
                    ) : (
                      <span className="note"> (no account — public link)</span>
                    )}
                  </dd>
                </div>
                {open.location && (
                  <div className="fact">
                    <dt>Location</dt>
                    <dd>{open.location}</dd>
                  </div>
                )}
                {open.sourceAlbum && (
                  <div className="fact">
                    <dt>From album</dt>
                    <dd>{open.sourceAlbum.label}</dd>
                  </div>
                )}
                <div className="fact">
                  <dt>Sent</dt>
                  <dd>{formatDateTime(open.uploadedAt)}</dd>
                </div>
                <div className="fact">
                  <dt>Taken</dt>
                  <dd>{formatDateTime(open.takenAt)}</dd>
                </div>
                {open.deletedAt !== null && (
                  <div className="fact">
                    <dt>Taken down</dt>
                    <dd>{formatDateTime(open.deletedAt)}</dd>
                  </div>
                )}
                <div className="fact">
                  <dt>Size</dt>
                  <dd>
                    {open.width}×{open.height}, {formatBytes(open.bytes)}
                  </dd>
                </div>
                <div className="fact">
                  <dt>Ids</dt>
                  <dd className="mono">
                    placement {shortId(open.id)} · asset{' '}
                    <a href={href('/photos', { assetId: open.assetId, filter: 'all' })}>
                      {shortId(open.assetId)}
                    </a>
                  </dd>
                </div>
              </dl>

              {error && (
                <p className="alert" role="alert">
                  {error}
                </p>
              )}

              <div className="row-tight">
                {open.deletedAt === null && (
                  <button
                    type="button"
                    className="button button-danger button-small"
                    disabled={busy}
                    onClick={() => void takeDown(open)}
                  >
                    Take down
                  </button>
                )}
                <button
                  type="button"
                  className="button button-quiet button-small"
                  onClick={() => setOpen(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </dialog>
    </>
  );
};
